import { useState } from "react";
import { Minus, Plus } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import useAdminLoad from "@/hooks/useAdminLoad";
import { Empty, Panel, Pill } from "@/components/admin/AdminUI";
import { genderLabel } from "@/components/admin/launch/GenderSplit";
import { formatDate, humanize } from "@/lib/format";

function List({ items, selected, setSelected, testId, extra, canSelect }) {
  if (!items.length) return <Empty title="No one here" testId={`${testId}-empty`} />;
  return (
    <ul className="max-h-[420px] divide-y divide-border overflow-y-auto" data-testid={testId}>
      {items.map((w) => (
        <li key={w.id} className="flex items-center gap-3 px-5 py-2.5 text-sm" data-testid={`${testId}-row-${w.id}`}>
          {canSelect && <Checkbox aria-label={`Select ${w.email}`} checked={selected.includes(w.id)} onCheckedChange={(v) => setSelected((s) => (v ? [...s, w.id] : s.filter((x) => x !== w.id)))} data-testid={`${testId}-select-${w.id}`} />}
          <span className="min-w-0 flex-1 truncate">{w.email}{w.is_test && <span className="cw-muted ml-2 text-xs" data-testid={`${testId}-test-note-${w.id}`}>Logged, not emailed</span>}</span>
          <span className="cw-soft w-28">{genderLabel(w.gender)}</span>
          <span className="cw-muted w-14 tabular-nums">{w.zip}</span>
          <span className="cw-muted hidden w-28 text-xs sm:block">{formatDate(w.created_at, false)}</span>
          {extra?.(w)}
        </li>
      ))}
    </ul>
  );
}

export default function WaveEntries({ wave, onChange }) {
  const draft = wave.status === "draft";
  const [rm, setRm] = useState([]);
  const [add, setAdd] = useState([]);
  const qs = new URLSearchParams({ status: "waiting", zip: wave.zips.join(","), limit: "200" });
  const { data: cand } = useAdminLoad(draft ? `/admin/waitlist?${qs}&_s=${wave.size}` : null);
  const entries = wave.entries || [];
  const candidates = (cand?.items || []).filter((w) => !w.wave_id);
  const codeTag = (w) => w.code_status && <Pill tone={w.code_status === "used" ? "success" : w.code_status === "active" ? "brand" : "neutral"} className="capitalize">{humanize(w.code_status)}</Pill>;

  return (
    <div className={`grid gap-6 ${draft ? "lg:grid-cols-2" : ""}`}>
      <Panel className="overflow-hidden" data-testid="wave-entries-panel">
        <div className="flex items-center justify-between gap-2 border-b border-border px-5 py-3">
          <p className="text-sm font-semibold">In this wave ({entries.length})</p>
          {draft && <Button size="sm" variant="outline" disabled={!rm.length} onClick={() => { onChange({ remove: rm }); setRm([]); }} data-testid="wave-remove-selected-button"><Minus />Remove {rm.length || ""}</Button>}
        </div>
        <List items={entries} selected={rm} setSelected={setRm} canSelect={draft} testId="wave-entries" extra={codeTag} />
      </Panel>
      {draft && (
        <Panel className="overflow-hidden" data-testid="wave-candidates-panel">
          <div className="flex items-center justify-between gap-2 border-b border-border px-5 py-3">
            <p className="text-sm font-semibold">Still waiting in these ZIPs ({candidates.length})</p>
            <Button size="sm" variant="outline" disabled={!add.length} onClick={() => { onChange({ add }); setAdd([]); }} data-testid="wave-add-selected-button"><Plus />Add {add.length || ""}</Button>
          </div>
          <List items={candidates} selected={add} setSelected={setAdd} canSelect testId="wave-candidates" />
        </Panel>
      )}
    </div>
  );
}
